
import React, { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Language } from '../translations';
import { Expedition } from '../types';
import { getLocalizedPath, formatDuration } from '../lib/i18n';
import { Loader2 } from 'lucide-react';
import BookingWidget from '../components/BookingWidget';
import { createPortal } from 'react-dom';

interface ExpeditionDetailProps {
    t: any;
    lang: Language;
    getExpField: <T>(exp: any, field: string, lang: Language) => T;
    overrideSlug?: string;
    expeditions?: Expedition[];
}

const EXP_DETAIL_I18N: Record<Language, {
    loading: string;
    not_found: string;
    best_season: string;
    choose_experiences: string;
    hourly_schedule: string;
    highlight: string;
    itinerary_weather_note: string;
    recommendations_gear: string;
    specific_policies: string;
    limit_reached: string;
    limit_desc: string;
    understood: string;
}> = {
    es: {
        loading: 'Cargando expedición...',
        not_found: 'Expedición no encontrada',
        best_season: 'Mejor época',
        choose_experiences: '* Elige hasta 3 experiencias para tu programa personalizado.',
        hourly_schedule: 'Cronograma y Actividades',
        highlight: 'Hito Destacado',
        itinerary_weather_note: '* Este itinerario está sujeto a cambios según las condiciones climáticas, priorizando siempre la seguridad del grupo.',
        recommendations_gear: 'Recomendaciones y Equipamiento',
        specific_policies: 'Políticas Específicas',
        limit_reached: 'Límite alcanzado',
        limit_desc: 'Solo puedes seleccionar un máximo de 3 experiencias para tu programa personalizado.',
        understood: 'Entendido'
    },
    en: {
        loading: 'Loading expedition...',
        not_found: 'Expedition not found',
        best_season: 'Best Season',
        choose_experiences: '* Choose up to 3 experiences for your customized program.',
        hourly_schedule: 'Schedule & Activities',
        highlight: 'Key Highlight',
        itinerary_weather_note: '* This itinerary is subject to change based on weather conditions, always prioritizing group safety.',
        recommendations_gear: 'Recommendations & Gear',
        specific_policies: 'Specific Policies',
        limit_reached: 'Limit Reached',
        limit_desc: 'You can only select up to 3 experiences for your personalized program.',
        understood: 'Understood'
    },
    pt: {
        loading: 'Carregando expedição...',
        not_found: 'Expedição não encontrada',
        best_season: 'Melhor época',
        choose_experiences: '* Escolha até 3 experiências para o seu programa personalizado.',
        hourly_schedule: 'Cronograma e Atividades',
        highlight: 'Destaque Principal',
        itinerary_weather_note: '* Este itinerário está sujeito a alterações de acordo com as condições climáticas, priorizando sempre a segurança do grupo.',
        recommendations_gear: 'Recomendações e Equipamento',
        specific_policies: 'Políticas Específicas',
        limit_reached: 'Limite atingido',
        limit_desc: 'Você só pode selecionar no máximo 3 experiências para o seu programa personalizado.',
        understood: 'Entendido'
    },
    fr: {
        loading: 'Chargement de l’expédition...',
        not_found: 'Expédition introuvable',
        best_season: 'Meilleure saison',
        choose_experiences: '* Choisissez jusqu’à 3 expériences pour votre programme personnalisé.',
        hourly_schedule: 'Programme et Activités',
        highlight: 'Point Fort',
        itinerary_weather_note: '* Cet itinéraire est susceptible d’être modifié selon les conditions météo, la sécurité du groupe restant prioritaire.',
        recommendations_gear: 'Recommandations et Équipement',
        specific_policies: 'Politiques Spécifiques',
        limit_reached: 'Limite atteinte',
        limit_desc: 'Vous ne pouvez sélectionner que 3 expériences au maximum pour votre programme personnalisé.',
        understood: 'Compris'
    },
    it: {
        loading: 'Caricamento spedizione...',
        not_found: 'Spedizione non trovata',
        best_season: 'Periodo migliore',
        choose_experiences: '* Scegli fino a 3 esperienze per il tuo programma personalizzato.',
        hourly_schedule: 'Programma e Attività',
        highlight: 'Punto Saliente',
        itinerary_weather_note: '* Questo itinerario è soggetto a modifiche in base alle condizioni meteorologiche, dando sempre priorità alla sicurezza del gruppo.',
        recommendations_gear: 'Raccomandazioni ed Equipaggiamento',
        specific_policies: 'Politiche Specifiche',
        limit_reached: 'Limite raggiunto',
        limit_desc: 'Puoi selezionare un massimo di 3 esperienze per il tuo programma personalizzato.',
        understood: 'Ho capito'
    },
    de: {
        loading: 'Expedition wird geladen...',
        not_found: 'Expedition nicht gefunden',
        best_season: 'Beste Reisezeit',
        choose_experiences: '* Wähle bis zu 3 Erlebnisse für dein individuelles Programm.',
        hourly_schedule: 'Zeitplan & Aktivitäten',
        highlight: 'Highlight',
        itinerary_weather_note: '* Dieser Ablauf kann wetterbedingt angepasst werden; die Sicherheit der Gruppe hat stets oberste Priorität.',
        recommendations_gear: 'Empfehlungen & Ausrüstung',
        specific_policies: 'Spezifische Richtlinien',
        limit_reached: 'Limit erreicht',
        limit_desc: 'Du kannst maximal 3 Erlebnisse für dein individuelles Programm auswählen.',
        understood: 'Verstanden'
    }
};

const ExpeditionDetail: React.FC<ExpeditionDetailProps> = ({ t, lang, getExpField, overrideSlug, expeditions = [] }) => {
    const detailI18n = EXP_DETAIL_I18N[lang] || EXP_DETAIL_I18N.es;
    const { slug: paramSlug } = useParams<{ slug: string }>();
    const slug = overrideSlug || paramSlug;
    const navigate = useNavigate();
    const exp = expeditions.find(e => 
        e.slug === slug || 
        e.id === slug || 
        (slug === 'wellness-day' && (e.id === 'wellness' || e.slug === 'wellness-day-patagonia')) ||
        (slug === 'wellness' && (e.id === 'wellness' || e.slug === 'wellness-day'))
    );
    const [selectedVariantId, setSelectedVariantId] = React.useState(exp?.variants?.[0]?.id || '');
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [isHovered, setIsHovered] = React.useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [selectedServices, setSelectedServices] = React.useState<string[]>([]);
    const [showLimitModal, setShowLimitModal] = React.useState(false);
    const [showRecs, setShowRecs] = React.useState(false);
    const [showPols, setShowPols] = React.useState(false);

    const handleServiceToggle = (service: string) => {
        if (selectedServices.includes(service)) {
            setSelectedServices(selectedServices.filter(s => s !== service));
        } else if (selectedServices.length < 3) {
            setSelectedServices([...selectedServices, service]);
        } else {
            setShowLimitModal(true);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        if (expeditions.length === 0) return; // Wait until Supabase data is loaded
        if (exp) {
            if (exp.variants && exp.variants.length > 0) {
                setSelectedVariantId(exp.variants[0].id);
            } else {
                setSelectedVariantId('');
            }
            setActiveIndex(0);
            // Reset scroll position on exp change
            if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft = 0;
        }
    }, [exp, expeditions.length]);

    // AUTO-PLAY LOGIC
    useEffect(() => {
        if (!exp?.gallery || exp.gallery.length <= 1 || isHovered) return;

        const interval = setInterval(() => {
            if (scrollContainerRef.current) {
                const container = scrollContainerRef.current;
                const maxScroll = container.scrollWidth - container.clientWidth;

                if (container.scrollLeft >= maxScroll - 10) {
                    container.scrollTo({ left: 0, behavior: 'auto' });
                } else {
                    container.scrollBy({ left: container.clientWidth, behavior: 'auto' });
                }
            }
        }, 6000);

        return () => clearInterval(interval);
    }, [exp?.gallery, isHovered]);

    const itinerary = useMemo(() => {
        if (!exp) return [];
        // First check if the selected variant has its own itinerary
        if (exp?.variants && selectedVariantId) {
            const variant = exp.variants.find((v: any) => v.id === selectedVariantId);
            const variantItinerary = variant ? getExpField<any[]>(variant, 'itinerary', lang) : undefined;
            if (variantItinerary) return variantItinerary;
        }
        // Fallback to the main expedition itinerary
        return getExpField<any[]>(exp, 'itinerary', lang) || [];
    }, [exp, selectedVariantId, lang, getExpField]);

    const currentPricingTiers = useMemo(() => {
        if (!exp) return [];
        let tiers = exp.pricing_tiers;
        if (exp.variants && selectedVariantId) {
            const variant = exp.variants.find((v: any) => v.id === selectedVariantId);
            if (variant) tiers = variant.pricing_tiers;
        }
        return tiers || [];
    }, [exp, selectedVariantId]);

    const variantTitle = useMemo(() => {
        if (!exp) return '';
        const titles: Record<string, Record<Language, string>> = {
            climbing: {
                es: 'Elige tu Escalada',
                en: 'Choose your Climbing Style',
                pt: 'Escolha sua Escalada',
                fr: 'Choisissez votre Escalade',
                it: 'Scegli la tua Scalata',
                de: 'Wählen Sie Ihren Kletterstil'
            },
            trekking: {
                es: 'Elige tu Ruta',
                en: 'Choose your Route',
                pt: 'Escolha sua Rota',
                fr: 'Choisissez votre Route',
                it: 'Scegli il tuo Percorso',
                de: 'Wählen Sie Ihre Route'
            },
            default: {
                es: 'Elige tu Opción',
                en: 'Choose your Option',
                pt: 'Escolha sua Opção',
                fr: 'Choisissez votre Option',
                it: 'Scegli la tua Opzione',
                de: 'Wählen Sie Ihre Option'
            }
        };
        const cat = exp.category === 'climbing' || exp.category === 'trekking' ? exp.category : 'default';
        return titles[cat]?.[lang] || titles[cat]?.['es'] || '';
    }, [exp?.category, lang]);

    const scrollToImage = (index: number) => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                left: scrollContainerRef.current.clientWidth * index,
                behavior: 'auto'
            });
        }
    };

    if (expeditions.length === 0) {
        return (
            <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 text-white">
                <Loader2 className="w-10 h-10 animate-spin text-[#00d4ff]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-gray-400 animate-pulse">
                    {detailI18n.loading}
                </span>
            </div>
        );
    }

    if (!exp) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6 text-white px-4 text-center">
                <p className="text-xl font-bold uppercase tracking-wider text-gray-300">
                    {detailI18n.not_found}
                </p>
                <Link
                    to={getLocalizedPath('/expeditions', lang)}
                    className="px-6 py-3 bg-[#00d4ff] text-black font-bold uppercase text-xs tracking-widest hover:bg-[#00d4ff]/80 transition-colors"
                >
                    {t.detail_back || 'Volver a Expediciones'}
                </Link>
            </div>
        );
    }

    return (
        <div className="animate-in pt-4 sm:pt-8 pb-12 px-4 sm:px-6 max-w-7xl mx-auto w-full">
            <Link to={getLocalizedPath('/expeditions', lang)} className="flex items-center space-x-2 text-[#00d4ff] uppercase text-[10px] font-bold tracking-widest mb-6 hover:translate-x-[-4px] transition-transform w-fit">
                <span>←</span> <span>{t.detail_back}</span>
            </Link>

            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-16 items-start w-full overflow-hidden">
                <div className="lg:col-span-2 w-full min-w-0 overflow-hidden">
                    <div className="mb-8 items-start w-full">
                        <p className="text-[#00d4ff] uppercase text-xs font-bold mb-4 leading-relaxed whitespace-normal break-words w-full">
                            {getExpField<string>(exp, 'catchphrase', lang)}
                        </p>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-6 leading-[0.95] tracking-tighter uppercase whitespace-normal break-words w-full font-title">
                            {getExpField<string>(exp, 'name', lang)}
                        </h1>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-6 py-6 border-y border-rock mb-10 uppercase text-[9px] md:text-[10px] font-bold tracking-widest text-gray-400">
                        <div className="flex flex-col gap-1">
                            <span className="text-[#00d4ff] text-[8px] opacity-70">{t.duration}</span>
                            <span className="text-white font-bold">{formatDuration(exp.duration, lang)}</span>
                        </div>
                        <div className="hidden sm:block w-px h-8 bg-rock/30"></div>
                        <div className="flex flex-col gap-1">
                            <span className="text-[#00d4ff] text-[8px] opacity-70">{t.difficulty}</span>
                            <span className="text-white font-bold">{t[`diff_${exp.difficulty}`]}</span>
                        </div>
                        {exp.distance && (
                            <>
                                <div className="hidden sm:block w-px h-8 bg-rock/30"></div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-[#00d4ff] text-[8px] opacity-70">{t.distance}</span>
                                    <span className="text-white font-bold">{exp.distance}</span>
                                </div>
                            </>
                        )}
                        {getExpField<string>(exp, 'best_season', lang) && (
                            <>
                                <div className="hidden sm:block w-px h-8 bg-rock/30"></div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-[#00d4ff] text-[8px] opacity-70">
                                        {detailI18n.best_season}
                                    </span>
                                    <span className="text-white font-bold">
                                        {getExpField<string>(exp, 'best_season', lang)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* GALLERY / CAROUSEL WITH PARALLAX */}
                    <div className="space-y-4 mb-12">
                        <div
                            className="relative group"
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            {/* MAIN SCROLL CONTAINER */}
                            <div
                                ref={scrollContainerRef}
                                className={`w-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide border border-rock bg-black scroll-smooth ${exp.gallery_layout === 'portrait' ? 'h-[500px] sm:h-[650px] md:h-[800px]' : 'h-[300px] sm:h-[400px] md:h-[500px]'}`}
                                onScroll={(e) => {
                                    const container = e.currentTarget;
                                    const index = Math.round(container.scrollLeft / container.clientWidth);
                                    if (exp.gallery && exp.gallery[index]) {
                                        if (activeIndex !== index) {
                                            setActiveIndex(index);
                                        }
                                    }
                                }}
                            >
                                {exp.gallery?.map((img, i) => {
                                    const src = typeof img === 'string' ? img : img.src;
                                    const alt = typeof img === 'string' ? `${getExpField(exp, 'name', lang)} ${i + 1}` : (lang === 'es' ? img.alt_es : img.alt_en);

                                    const isPortrait = exp.gallery_layout === 'portrait';
                                    const isContain = exp.gallery_layout === 'contain';
                                    const showFit = isPortrait || isContain;

                                    return (
                                        <div key={`${exp.id}-slide-${src}-${i}`} className="w-full min-w-full h-full flex-shrink-0 snap-center overflow-hidden relative flex items-center justify-center bg-black">
                                            {/* Static background for portrait/contain to fill gaps */}
                                            {showFit && (
                                                <img
                                                    src={src}
                                                    className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-20 scale-110"
                                                    alt=""
                                                />
                                            )}

                                            <img
                                                src={src}
                                                className={`transition-opacity duration-300 ${showFit ? 'relative w-full h-full object-contain z-10' : 'absolute inset-0 w-full h-full object-cover'}`}
                                                style={{
                                                    objectPosition: typeof img === 'object' && img.position ? img.position : 'center'
                                                }}
                                                alt={alt}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            {/* PUNTITOS INDICADORES */}
                            {exp.gallery && exp.gallery.length > 1 && (
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                                    {exp.gallery?.map((img, i) => {
                                        return (
                                            <button
                                                key={`${exp.id}-dot-${i}`}
                                                onClick={() => scrollToImage(i)}
                                                className={`w-2 h-2 rounded-full transition-all duration-300 ${i === activeIndex
                                                    ? 'bg-[#00d4ff] scale-125'
                                                    : 'bg-white/30 hover:bg-white/50'
                                                    }`}
                                                aria-label={`Go to slide ${i + 1}`}
                                            />
                                        );
                                    })}
                                </div>
                            )}

                            {/* NAV ARROWS */}
                            {exp.gallery && exp.gallery.length > 1 && (
                                <>
                                    <button
                                        onClick={() => {
                                            if (scrollContainerRef.current && exp.gallery) {
                                                const container = scrollContainerRef.current;
                                                const currentIndex = Math.round(container.scrollLeft / container.clientWidth);
                                                const nextIndex = currentIndex === 0 ? exp.gallery.length - 1 : currentIndex - 1;
                                                scrollToImage(nextIndex);
                                            }
                                        }}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 border border-white/20 rounded-full flex items-center justify-center text-white scale-0 group-hover:scale-100 transition-transform hover:bg-[#00d4ff] hover:border-[#00d4ff] z-20"
                                    >
                                        ←
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (scrollContainerRef.current && exp.gallery) {
                                                const container = scrollContainerRef.current;
                                                const currentIndex = Math.round(container.scrollLeft / container.clientWidth);
                                                const nextIndex = currentIndex === exp.gallery.length - 1 ? 0 : currentIndex + 1;
                                                scrollToImage(nextIndex);
                                            }
                                        }}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 border border-white/20 rounded-full flex items-center justify-center text-white scale-0 group-hover:scale-100 transition-transform hover:bg-[#00d4ff] hover:border-[#00d4ff] z-20"
                                    >
                                        →
                                    </button>
                                </>
                            )}
                        </div>

                        {/* THUMBNAILS NAVIGATION */}
                        {exp.gallery && exp.gallery.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                {exp.gallery?.map((img, i) => {
                                    const src = typeof img === 'string' ? img : img.src;
                                    return (
                                        <button
                                            key={`${exp.id}-thumb-${src}-${i}`}
                                            onClick={() => scrollToImage(i)}
                                            className={`relative min-w-[100px] h-20 border-2 transition-all duration-300 overflow-hidden ${activeIndex === i ? 'border-[#00d4ff] opacity-100' : 'border-rock opacity-40 hover:opacity-70'}`}
                                        >
                                            <img
                                                src={src}
                                                className="w-full h-full object-cover"
                                                alt={`Thumbnail ${i + 1}`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <p className="text-lg md:text-xl text-blue-200/50 font-light leading-relaxed italic mb-12 tracking-tight break-words">
                        {getExpField<string>(exp, 'desc', lang) || ''}
                    </p>

                    {exp.services_es && (
                        <div className="mb-16">
                            <h3 className="text-sm font-bold text-[#00d4ff] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                <span className="w-8 h-px bg-[#00d4ff]"></span>
                                {t.services_title}
                            </h3>
                            {exp.id === 'wellness' && (
                                <p className="text-gray-400 text-xs italic mb-8 uppercase tracking-widest font-light">
                                    {detailI18n.choose_experiences}
                                </p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                {exp.detailed_services_es ? (
                                    getExpField<{name: string, benefit: string}[]>(exp, 'detailed_services', lang)?.map((service, i) => {
                                        const isSelected = selectedServices.includes(service.name);
                                        return (
                                            <div 
                                                key={i} 
                                                onClick={() => handleServiceToggle(service.name)}
                                                className={`flex flex-col gap-2 border p-6 cursor-pointer transition-all duration-300 group ${isSelected ? 'bg-[#00d4ff]/10 border-[#00d4ff]/50' : 'bg-stone/20 border-rock hover:border-[#00d4ff]'}`}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-1.5 h-1.5 rounded-full transition-colors ${isSelected ? 'bg-[#00d4ff]' : 'bg-[#00d4ff]/40 group-hover:bg-[#00d4ff]'}`}></div>
                                                        <span className={`text-xs uppercase tracking-[0.2em] font-bold transition-colors ${isSelected ? 'text-white' : 'text-gray-300 group-hover:text-[#00d4ff]'}`}>{service.name}</span>
                                                    </div>
                                                    {exp.id === 'wellness' && (
                                                        <div className={`w-4 h-4 border flex items-center justify-center transition-all ${isSelected ? 'bg-[#00d4ff] border-[#00d4ff]' : 'border-rock group-hover:border-[#00d4ff]'}`}>
                                                            {isSelected && <span className="text-black text-[10px] font-bold">✓</span>}
                                                        </div>
                                                    )}
                                                </div>
                                                <p className={`text-[10px] md:text-xs font-light leading-relaxed pl-4.5 transition-colors ${isSelected ? 'text-gray-300' : 'text-gray-400'}`}>
                                                    {service.benefit}
                                                </p>
                                            </div>
                                        );
                                    })
                                ) : (
                                    getExpField<string[]>(exp, 'services', lang)?.map((service, i) => (
                                        <div key={i} className="flex items-center gap-4 bg-stone/20 border border-rock p-4 group hover:border-[#00d4ff] transition-colors">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[#00d4ff]"></div>
                                            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-300">{service}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {exp.variants && (
                        <div className="mb-16">
                            <h3 className="text-sm font-bold text-[#00d4ff] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                <span className="w-8 h-px bg-[#00d4ff]"></span>
                                {variantTitle}
                            </h3>
                            <div className="grid gap-4">
                                {exp.variants.map((v, i) => (
                                    <div
                                        key={i}
                                        onClick={() => setSelectedVariantId(v.id)}
                                        className={`bg-stone/30 border p-6 transition-all group cursor-pointer ${selectedVariantId === v.id ? 'border-[#00d4ff] bg-[#00d4ff]/5' : 'border-rock hover:border-[#00d4ff]/50'}`}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className={`text-xl font-title uppercase transition-colors ${selectedVariantId === v.id ? 'text-[#00d4ff]' : 'text-white group-hover:text-[#00d4ff]'}`}>{getExpField<string>(v, 'name', lang)}</h4>
                                            <span className={`text-[10px] px-3 py-1 font-bold uppercase tracking-widest ${selectedVariantId === v.id ? 'bg-[#00d4ff] text-black' : 'bg-[#00d4ff]/10 text-[#00d4ff]'}`}>{v.duration}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 italic font-light tracking-wide">{v.info}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {currentPricingTiers && currentPricingTiers.length > 0 && (
                        <div className="mb-16">
                            <h3 className="text-sm font-bold text-[#00d4ff] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                <span className="w-8 h-px bg-[#00d4ff]"></span>
                                {t.pricing_title}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 overflow-hidden border border-rock">
                                {currentPricingTiers.map((tier, idx) => (
                                    <div key={idx} className="bg-stone/50 p-6 text-center border-b sm:border-r border-rock last:border-0 last:border-r-0">
                                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">{tier.min_people}+ {t.pricing_pax}</p>
                                        <p className="text-2xl font-title text-white">USD${tier.price_per_person}</p>
                                        <p className="text-[8px] text-[#00d4ff] uppercase font-bold tracking-tighter">{t.pricing_per_person}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {exp.meal_plan && (
                        <div className="mb-24">
                            <h3 className="text-sm font-bold text-[#00d4ff] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                <span className="w-8 h-px bg-[#00d4ff]"></span>
                                {t.nutrition_title}
                            </h3>
                            <div className="border border-rock bg-stone/30 overflow-hidden">
                                <div className="hidden sm:grid grid-cols-3 bg-[#1a3a3a] border-b border-rock text-[10px] font-bold uppercase tracking-widest p-4">
                                    <div className="text-[#00d4ff]">{t.nutrition_meal}</div>
                                    <div className="text-[#00d4ff]">{t.nutrition_example}</div>
                                    <div className="text-[#00d4ff]">{t.nutrition_note}</div>
                                </div>
                                {exp.meal_plan.map((item, idx) => (
                                    <div key={idx} className="flex flex-col sm:grid sm:grid-cols-3 border-b border-rock last:border-0 hover:bg-white/5 transition-colors">
                                        <div className="p-4 border-b sm:border-b-0 sm:border-r border-rock bg-stone/40 text-[10px] font-bold uppercase tracking-widest flex items-center gap-3">
                                            <span className="sm:hidden text-gray-600 text-[8px]">{t.nutrition_meal}</span>
                                            {item.meal}
                                        </div>
                                        <div className="p-4 border-b sm:border-b-0 sm:border-r border-rock text-xs text-gray-400 font-light italic flex items-center leading-relaxed gap-3">
                                            <span className="sm:hidden text-gray-600 text-[8px]">{t.nutrition_example}</span>
                                            {item.example}
                                        </div>
                                        <div className="p-4 text-[10px] text-gray-500 font-bold uppercase tracking-tighter flex items-center leading-tight gap-3">
                                            <span className="sm:hidden text-gray-600 text-[8px]">{t.nutrition_note}</span>
                                            {item.note}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {itinerary && itinerary.length > 0 && exp.itinerary_type !== 'none' && exp.id !== 'wellness' && (
                        <div className="mb-16">
                            <h3 className="text-4xl mb-8 uppercase tracking-tighter">
                                {exp.itinerary_type === 'hourly' 
                                    ? detailI18n.hourly_schedule
                                    : t.itinerary
                                }
                            </h3>
                            <div className="space-y-4 md:space-y-6">
                                {itinerary.map((day: any, i: number) => (
                                    <div key={i} className="relative pl-0 py-1 group">
                                        <div className="flex flex-col md:flex-row gap-2 md:gap-12 items-start">
                                            {/* Time/Day Indicator */}
                                            <div className="w-full md:w-32 flex-shrink-0 pt-0">
                                                <div className="text-3xl md:text-4xl font-title text-[#00d4ff] leading-none select-none opacity-60 group-hover:opacity-100 transition-opacity">
                                                    {day.time ? day.time.split(' ')[0] : (day.day < 10 ? `0${day.day}` : day.day)}
                                                    {day.time && <span className="text-sm md:text-base ml-1 opacity-50">{day.time.split(' ')[1]}</span>}
                                                </div>
                                            </div>

                                            {/* Content Section */}
                                            <div className="flex-1 pb-2 md:pb-4 relative w-full">
                                                {/* Connecting node (bullet) */}
                                                <div className="absolute -left-4 md:-left-6 top-3 w-1.5 h-1.5 bg-[#00d4ff] hidden md:block opacity-30 group-hover:opacity-100 transition-opacity"></div>

                                                {day.title && (
                                                    <h4 className="text-xl md:text-2xl font-title uppercase text-white group-hover:text-[#00d4ff] transition-colors mb-1 tracking-tight">
                                                        {day.title}
                                                    </h4>
                                                )}

                                                {day.route && <p className="text-[9px] text-gray-500 font-bold uppercase tracking-[0.2em] mb-2">{day.route}</p>}

                                                {day.distance && (
                                                    <div className="flex items-center gap-2 mb-2 md:mb-6">
                                                        <span className="text-[#00d4ff] text-[8px] font-bold uppercase tracking-widest">{t.distance}:</span>
                                                        <span className="text-white text-[10px] font-bold tracking-widest uppercase">{day.distance}</span>
                                                    </div>
                                                )}

                                                <p className="text-gray-400 leading-relaxed font-light text-sm italic mb-2 md:mb-4 max-w-2xl">
                                                    {day.desc}
                                                </p>

                                                {day.hito && (
                                                    <div className="bg-stone/20 border-l-2 border-[#00d4ff]/30 p-6 group-hover:border-[#00d4ff] transition-colors max-w-2xl">
                                                        <span className="text-[#00d4ff] text-[8px] font-bold uppercase tracking-[0.3em] mb-2 block">{detailI18n.highlight}</span>
                                                        <p className="text-white text-base font-bold uppercase tracking-tight leading-tight">
                                                            {day.hito}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-gray-500 italic mt-8 uppercase tracking-widest">
                                {detailI18n.itinerary_weather_note}
                            </p>
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-8 mb-20">
                        <div className="bg-stone p-6 border border-rock">
                            <h3 className="text-2xl mb-6 uppercase text-[#00d4ff] border-b border-rock pb-3 tracking-tighter">{t.detail_includes}</h3>
                            <ul className="space-y-4">
                                {(getExpField<string[]>(exp, 'includes', lang) || []).map((item, i) => (
                                        <li key={i} className="flex items-start space-x-3">
                                            <span className="text-[#00d4ff] font-bold text-xs">✓</span>
                                            <span className="text-gray-400 uppercase text-[10px] tracking-widest font-bold pt-0.5">{item}</span>
                                        </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-stone p-6 border border-rock">
                            <h3 className="text-2xl mb-6 uppercase text-red-500 border-b border-rock pb-3 tracking-tighter">{t.detail_not_includes}</h3>
                            <ul className="space-y-4">
                                {(getExpField<string[]>(exp, 'excludes', lang) || []).map((item, i) => (
                                    <li key={i} className="flex items-start space-x-3">
                                        <span className="text-red-500 font-bold text-xs">✗</span>
                                        <span className="text-gray-400 uppercase text-[10px] tracking-widest font-bold pt-0.5">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Recommendations and Policies Accordions */}
                    {(() => {
                        const recs = getExpField<string[]>(exp, 'recommendations', lang) || [];
                        const pols = getExpField<string[]>(exp, 'policies', lang) || [];
                        if (recs.length === 0 && pols.length === 0) return null;
                        return (
                            <div className="space-y-4 mb-20 border-t border-rock pt-10">
                                {recs.length > 0 && (
                                    <div className="border border-rock bg-stone/20 rounded-[10px] overflow-hidden">
                                        <button 
                                            onClick={() => setShowRecs(!showRecs)}
                                            className="w-full flex items-center justify-between p-6 text-left font-title text-xl uppercase tracking-wider text-white hover:bg-white/5 transition-all outline-none"
                                        >
                                            <span>{detailI18n.recommendations_gear}</span>
                                            <span className="text-[#00d4ff] text-2xl leading-none">{showRecs ? '−' : '+'}</span>
                                        </button>
                                        {showRecs && (
                                            <div className="px-6 pb-6 pt-2 border-t border-rock/30 space-y-3 animate-in fade-in duration-300">
                                                {recs.map((rec, i) => (
                                                    <div key={i} className="flex items-start gap-3">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-[#00d4ff] mt-2"></div>
                                                        <p className="text-gray-400 uppercase text-[10px] tracking-widest font-bold leading-relaxed">{rec}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {pols.length > 0 && (
                                    <div className="border border-rock bg-stone/20 rounded-[10px] overflow-hidden">
                                        <button 
                                            onClick={() => setShowPols(!showPols)}
                                            className="w-full flex items-center justify-between p-6 text-left font-title text-xl uppercase tracking-wider text-white hover:bg-white/5 transition-all outline-none"
                                        >
                                            <span>{detailI18n.specific_policies}</span>
                                            <span className="text-[#00d4ff] text-2xl leading-none">{showPols ? '−' : '+'}</span>
                                        </button>
                                        {showPols && (
                                            <div className="px-6 pb-6 pt-2 border-t border-rock/30 space-y-3 animate-in fade-in duration-300">
                                                {pols.map((pol, i) => (
                                                    <div key={i} className="flex items-start gap-3">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-red-500/80 mt-2"></div>
                                                        <p className="text-gray-400 uppercase text-[10px] tracking-widest font-bold leading-relaxed">{pol}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>

                <div className="lg:sticky lg:top-24">
                    <BookingWidget
                        exp={exp}
                        t={t}
                        lang={lang}
                        selectedVariantId={selectedVariantId}
                        onVariantChange={setSelectedVariantId}
                        selectedServices={selectedServices}
                        onServiceToggle={handleServiceToggle}
                    />
                </div>
            </div>

            {showLimitModal && createPortal(
                <div 
                    className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-300"
                    onClick={() => setShowLimitModal(false)}
                >
                    <div 
                        className="bg-stone border border-[#00d4ff] p-8 md:p-12 max-w-md w-full text-center space-y-6 shadow-[0_0_50px_rgba(0,212,255,0.2)] animate-in zoom-in-95 duration-300"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="w-16 h-16 bg-[#00d4ff]/10 border border-[#00d4ff] rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-[#00d4ff] text-2xl font-bold">!</span>
                        </div>
                        <h3 className="text-2xl font-title uppercase tracking-tight text-white">
                            {detailI18n.limit_reached}
                        </h3>
                        <p className="text-gray-400 text-xs uppercase tracking-[0.2em] leading-relaxed font-bold">
                            {detailI18n.limit_desc}
                        </p>
                        <button 
                            onClick={() => setShowLimitModal(false)}
                            className="w-full bg-[#00d4ff] text-black h-12 uppercase font-bold tracking-widest text-[10px] hover:bg-white transition-colors"
                        >
                            {detailI18n.understood}
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ExpeditionDetail;
